import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sparkles,
  Timer,
  Check,
  Clock,
  Calendar,
  Video,
  Mic,
  FileText,
} from 'lucide-react';

// Set release date to Mar 31, 2025 11:00 PM IST
const RELEASE_DATE = new Date('2025-03-31T23:00:00+05:30');

interface Feature {
  title: string;
  description: string;
  isNew?: boolean;
}

const currentFeatures: Feature[] = [
  {
    title: 'Advanced Booking Management',
    description:
      'Complete scheduling system with customizable event types, booking pages, and calendar integration.',
    isNew: true,
  },
  {
    title: 'Personal Booking Links',
    description:
      'Unique, shareable booking links with custom URLs (/:username/:eventTypeSlug) for easy appointment scheduling.',
    isNew: true,
  },
  {
    title: 'Flexible Scheduling',
    description:
      'Set up custom availability schedules with time zone detection and buffer times between appointments.',
    isNew: true,
  },
  {
    title: 'Booking Rescheduling',
    description:
      'Allow users to reschedule appointments with automated notifications and calendar updates.',
    isNew: false,
  },
  {
    title: 'Multi-Host Support',
    description:
      'Configure event types with multiple hosts for team-based scheduling and round-robin assignment.',
    isNew: false,
  },
  {
    title: 'Organization Management',
    description:
      'Manage teams within organizations, with role-based permissions and shared scheduling resources.',
    isNew: false,
  },
];

const upcomingFeatures: Feature[] = [
  {
    title: 'AI Meeting Recording & Transcription',
    description:
      'Automatically record, transcribe, and summarize appointments with advanced AI processing.',
  },
  {
    title: 'Smart Meeting Insights',
    description:
      'AI-powered analysis of meeting transcripts providing actionable insights and follow-up items.',
  },
  {
    title: 'Automated Meeting Notes',
    description:
      'Generate comprehensive meeting notes and action items from transcripts using AI technology.',
  },
  {
    title: 'Customizable Booking Pages',
    description:
      'Fully customizable booking pages with advanced branding options and personalized user experience.',
  },
  {
    title: 'Advanced Availability Rules',
    description:
      'Set complex availability rules including rolling availability, seasonal schedules, and capacity limits.',
  },
  {
    title: 'Workflow Automations',
    description:
      'Create custom workflows and automations triggered by scheduling events and meeting outcomes.',
  },
];

export function WhatsNewModal({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [timeLeft, setTimeLeft] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  }>({ hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const distance = RELEASE_DATE.getTime() - now.getTime();

      if (distance > 0) {
        setTimeLeft({
          hours: Math.floor(distance / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      } else {
        clearInterval(timer);
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Sparkles className="h-5 w-5 text-purple-500" />
            What's New in Bleep
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4 flex flex-col gap-8">
          {/* Countdown Timer */}
          <div className="rounded-lg bg-purple-50 p-6 dark:bg-purple-950/50">
            <h3 className="flex items-center gap-2 text-lg font-semibold text-purple-700 dark:text-purple-300">
              <Timer className="h-5 w-5" />
              Next Release Coming Soon
            </h3>
            <div className="mt-4 flex items-center justify-center gap-6 text-purple-600 dark:text-purple-400">
              <div className="text-center">
                <div className="text-4xl font-bold">{timeLeft.hours}</div>
                <div className="text-sm">hours</div>
              </div>
              <div className="text-2xl font-bold">:</div>
              <div className="text-center">
                <div className="text-4xl font-bold">{timeLeft.minutes}</div>
                <div className="text-sm">minutes</div>
              </div>
              <div className="text-2xl font-bold">:</div>
              <div className="text-center">
                <div className="text-4xl font-bold">{timeLeft.seconds}</div>
                <div className="text-sm">seconds</div>
              </div>
            </div>
          </div>

          <ScrollArea className="h-[500px] pr-4">
            {/* Current Features */}
            <div className="mb-8">
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                <Check className="h-5 w-5 text-green-500" />
                Current Features
              </h3>
              <div className="grid gap-4">
                {currentFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="rounded-lg border bg-card p-4 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-medium">{feature.title}</h4>
                      {feature.isNew && (
                        <Badge className="bg-green-500">New</Badge>
                      )}
                    </div>
                    <p className="mt-2 text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Features */}
            <div>
              <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold">
                <Clock className="h-5 w-5 text-blue-500" />
                Coming Soon
              </h3>
              <div className="grid gap-4">
                {upcomingFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className="rounded-lg border bg-muted/50 p-6 shadow-sm"
                  >
                    <h4 className="text-lg font-medium">{feature.title}</h4>
                    <p className="mt-2 text-muted-foreground">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
